import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Clock, ArrowRight, Wrench } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingCallBar from "@/components/FloatingCallBar";
import Seo from "@/components/Seo";
import { GUIDES, SERVICE_PAGES, PHONE_DISPLAY, PHONE_TEL } from "@/constants/site";

export default function GuidePage() {
  const { slug } = useParams();
  const guide = GUIDES.find((g) => g.slug === slug);

  if (!guide) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#FAFAFA] px-4 text-center">
        <h1 className="font-display text-2xl font-bold text-navy">Guide not found</h1>
        <Link to="/" className="mt-4 rounded-full bg-rescue px-6 py-3 text-sm font-bold text-white">
          Back to homepage
        </Link>
      </div>
    );
  }

  const related = guide.relatedService ? SERVICE_PAGES[guide.relatedService] : null;

  return (
    <div className="bg-[#FAFAFA] pb-16 md:pb-0" data-testid={`guide-page-${guide.slug}`}>
      <Seo title={guide.metaTitle} description={guide.metaDesc} canonical={`https://gtaappliancerescue.ca/guides/${guide.slug}`} />
      <Header />
      <main className="pt-36 sm:pt-40">
        <article className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-rescue"
          >
            <Wrench className="h-3.5 w-3.5" />
            Repair Guide
            <span className="flex items-center gap-1 text-slate-400">
              <Clock className="h-3 w-3" /> {guide.readTime}
            </span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-5 font-display text-3xl font-black leading-[1.1] tracking-tight text-navy sm:text-4xl"
          >
            {guide.h1}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            {guide.intro}
          </motion.p>

          <div className="mt-10 space-y-8">
            {guide.sections.map((section, i) => (
              <motion.section
                key={section.h2}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: 0.05 * (i % 3) }}
                data-testid={`guide-section-${i}`}
              >
                <h2 className="font-display text-xl font-bold text-navy sm:text-2xl">{section.h2}</h2>
                <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">{section.body}</p>
              </motion.section>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-12 rounded-2xl bg-navy p-7 text-white sm:p-8"
            data-testid="guide-cta"
          >
            <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-red-400">
              Rather Have a Pro Handle It?
            </p>
            <p className="mt-3 font-display text-xl font-bold sm:text-2xl">
              Same-day service across Toronto & the GTA.
            </p>
            <p className="mt-2 text-sm text-slate-300">
              $80 in-person diagnosis — waived when you proceed with the repair. 1-year parts & 30-day labour warranty.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <a
                href={PHONE_TEL}
                data-testid="guide-call-button"
                className="flex items-center justify-center gap-2 rounded-full bg-rescue px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-rescue-dark"
              >
                <Phone className="h-4 w-4" /> Call {PHONE_DISPLAY}
              </a>
              {related && (
                <Link
                  to={`/services/${guide.relatedService}`}
                  data-testid="guide-service-link"
                  className="flex items-center justify-center gap-2 rounded-full border-2 border-white/30 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:border-white hover:bg-white/10"
                >
                  {related.h1.replace(" in Toronto & the GTA", "")} Details
                  <ArrowRight className="h-4 w-4" />
                </Link>
              )}
            </div>
          </motion.div>
        </article>
      </main>
      <div className="mt-20">
        <Footer />
      </div>
      <FloatingCallBar />
    </div>
  );
}
